import { Theme, Users } from "@just-in-types";
import * as React from "react";
import styled from "react-emotion";
import { connect } from "react-redux";
import Button from "../components/Button";
import Column from "../components/Column";
import Container from "../components/Container";
import Input from "../components/Input";
import Keypad from "../components/Keypad";
import NextButton from "../components/NextButton";
import Row from "../components/Row";
import Title from "../components/Title";
import ErrorBoundary from "../hoc/withErrorBoundary";
import { RouteComponentProps } from "../Site";
import { navigateToNext, NavigateToNext } from "../store/actions";
import { IStrings } from "../store/reducers/strings";

interface ISupercardDispatchProps {
  onNavigateToNext: NavigateToNext;
}
interface ISupercardStateProps {
  languageStrings: IStrings;
  theme: Theme.Props;
  user: Users.User;
}
type SupercardProps = RouteComponentProps & ISupercardDispatchProps & ISupercardStateProps;
interface ISupercardState {
  sending: boolean;
  supercard: string;
  supercardIsValid: boolean;
}
type SupercardState = ISupercardState;

const SupercardContainer = styled(Container)``;
const SupercardInput = styled(Input)`
  font-family: monospace;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 4rem;
  width: 14rem;
  margin: 0;
  font-size: 1.5rem;
  border: 2px solid black;
  border-right-width: 1px;
  border-radius: 12px 0 0 12px;
  padding-left: 0.5rem;
`;
const BackArrow = () => (
  <svg width="2rem" height="2rem" fill="white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
    <path d="M257.5 445.1l-22.2 22.2c-9.4 9.4-24.6 9.4-33.9 0L7 273c-9.4-9.4-9.4-24.6 0-33.9L201.4 44.7c9.4-9.4 24.6-9.4 33.9 0l22.2 22.2c9.5 9.5 9.3 25-.4 34.3L136.6 216H424c13.3 0 24 10.7 24 24v32c0 13.3-10.7 24-24 24H136.6l120.5 114.8c9.8 9.3 10 24.8.4 34.3z" />
  </svg>
);

const BackButton = styled(Button)`
  width: 4rem;
  height: 4rem;
  padding: 0.9rem;
`;

class Supercard extends React.Component<SupercardProps, SupercardState> {
  public state: SupercardState = {
    sending: false,
    supercard: "",
    supercardIsValid: false,
  };

  public keypadValidator = (supercard: string, value: string) => {
    let newSupercard;

    if (supercard.length < 5 && value.match(/\d/g)) {
      newSupercard = supercard + value;

      if (newSupercard.length === 5) {
        this.setState({ supercardIsValid: true });
      }

      return newSupercard;
    }

    if (value === "clear") {
      this.setState({ supercardIsValid: false });

      return "";
    }

    if (value === "backspace") {
      newSupercard = supercard.slice(0, -1);

      this.setState({ supercardIsValid: false });

      return newSupercard;
    }

    return supercard;
  };

  public handleKeypadButton = (event: React.MouseEvent<HTMLButtonElement>) => {
    const { value } = event.currentTarget;

    const supercard = this.keypadValidator(this.state.supercard, value);

    this.setState({ supercard });
  };

  public handleInput = ({ currentTarget: { value } }: React.FormEvent<HTMLInputElement>) =>
    this.setState({ supercard: value });

  public render() {
    const {
      languageStrings: strings,
      routes: {
        supercard: { optional },
      },
      theme: { colors },
      site: {
        kioskUiBackgroundColor,
        kioskUiTitleTextColor,
        kioskUiButtonPrimaryColor,
        kioskUiButtonErrorColor,
        kioskUiButtonSuccessColor,
      },
      site,
      user,
    } = this.props;
    const { supercard, supercardIsValid, sending } = this.state;
    const supercardPlaceholder = "xxxxx";

    return (
      <ErrorBoundary>
        <SupercardContainer style={{ background: kioskUiBackgroundColor }}>
          <Row align="center" justify="center">
            <Title color={kioskUiTitleTextColor ? kioskUiTitleTextColor : colors.secondary}>
              {strings.supercardHeader}
            </Title>
          </Row>
          <Column align="center" justify="space-around">
            <Row>
              <SupercardInput
                type="tel"
                onChange={this.handleInput}
                placeholder={supercardPlaceholder}
                value={supercard}
              />
              <BackButton
                bg={kioskUiButtonPrimaryColor ? kioskUiButtonPrimaryColor : colors.primary}
                borderRadius="right"
                disabled={sending}
                margin="0"
                onClick={this.handleKeypadButton}
                size="2rem"
                value="backspace"
              >
                <BackArrow />
              </BackButton>
            </Row>
            <Keypad
              colors={colors}
              disabled={sending}
              isUppercase={false}
              onHandleKeypadClick={this.handleKeypadButton}
              type="numbers"
              site={site}
            />
          </Column>
          <Row align="center" justify="center">
            {optional && (
              <NextButton
                bg={kioskUiButtonErrorColor ? kioskUiButtonErrorColor : colors.error}
                disabled={false}
                label={"No Thanks"}
                name={null}
                value={null}
              />
            )}
            <NextButton
              bg={kioskUiButtonSuccessColor ? kioskUiButtonSuccessColor : colors.success}
              disabled={!supercardIsValid}
              name="supercard"
              value={supercard}
            >
              {strings.addSupercard}
            </NextButton>
          </Row>
        </SupercardContainer>
      </ErrorBoundary>
    );
  }
}

const mapStateToProps = ({ router: { languageStrings }, theme, user }) => ({
  languageStrings,
  theme,
  user,
});

const mapDispatchToProps = {
  onNavigateToNext: navigateToNext,
};

export default connect<ISupercardStateProps, ISupercardDispatchProps>(mapStateToProps, mapDispatchToProps)(Supercard);



// WEBPACK FOOTER //
// ./src/views/Supercard.tsx